import { countText, decodeUtf8File, validateTextFile } from "./data-tool-core.js";
import { validateJsonAgainstSchema } from "./json-schema-validator-core.js";
import "./data-tool.css";

const root = document.querySelector("[data-schema-validator]");
if (root) {
  const ids = [
    "schemaStatus", "schemaDataFile", "chooseSchemaDataFile", "schemaData", "schemaDataName", "schemaDataCount",
    "schemaDocumentFile", "chooseSchemaDocumentFile", "schemaDocument", "schemaDocumentName", "schemaDocumentCount",
    "loadSchemaExample", "clearSchemaValidator", "validateJsonSchema", "schemaResults", "schemaResultMark",
    "schemaResultsTitle", "schemaResultSummary", "schemaIssueList"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));

  function showStatus(message = "", kind = "error") {
    elements.schemaStatus.textContent = message;
    elements.schemaStatus.dataset.kind = kind;
    elements.schemaStatus.hidden = !message;
    if (message && kind === "error") window.toolboxAnalytics?.track("tool_error", { error_category: "validation" });
  }

  function countLabel(value) {
    const count = countText(value);
    return `${count.characters.toLocaleString("en-US")} characters · ${count.lines.toLocaleString("en-US")} lines`;
  }

  function changed() {
    showStatus();
    elements.schemaResults.hidden = true;
    elements.schemaDataCount.textContent = countLabel(elements.schemaData.value);
    elements.schemaDocumentCount.textContent = countLabel(elements.schemaDocument.value);
  }

  async function loadFile(file, target, nameElement, input) {
    const error = validateTextFile(file, ["json"], "JSON");
    if (error) return showStatus(error);
    try {
      target.value = decodeUtf8File(new Uint8Array(await file.arrayBuffer()));
      nameElement.textContent = file.name;
      changed();
      target.focus();
    } catch (error) {
      showStatus(error?.message || "This JSON file could not be read.");
    } finally {
      input.value = "";
    }
  }

  function validate() {
    showStatus();
    const result = validateJsonAgainstSchema(elements.schemaData.value, elements.schemaDocument.value);
    if (result.error) {
      elements.schemaResults.hidden = true;
      showStatus(result.error);
      return;
    }
    elements.schemaIssueList.replaceChildren();
    elements.schemaResults.dataset.valid = String(result.valid);
    elements.schemaResultMark.textContent = result.valid ? "✓" : "!";
    elements.schemaResultsTitle.textContent = result.valid ? "JSON matches the schema" : "JSON does not match the schema";
    elements.schemaResultSummary.textContent = result.valid
      ? "No validation issues found."
      : `${result.issues.length.toLocaleString("en-US")} ${result.issues.length === 1 ? "issue" : "issues"}${result.truncated ? " shown" : ""}`;
    for (const issue of result.issues) {
      const item = document.createElement("li");
      const path = document.createElement("code");
      path.textContent = issue.path;
      const keyword = document.createElement("span");
      keyword.className = "schema-issue-keyword";
      keyword.textContent = issue.keyword;
      const message = document.createElement("span");
      message.textContent = issue.message;
      item.append(path, keyword, message);
      elements.schemaIssueList.append(item);
    }
    elements.schemaResults.hidden = false;
    window.toolboxAnalytics?.track("tool_complete");
    elements.schemaResultsTitle.focus();
  }

  elements.chooseSchemaDataFile.addEventListener("click", () => elements.schemaDataFile.click());
  elements.chooseSchemaDocumentFile.addEventListener("click", () => elements.schemaDocumentFile.click());
  elements.schemaDataFile.addEventListener("change", () => loadFile(elements.schemaDataFile.files?.[0], elements.schemaData, elements.schemaDataName, elements.schemaDataFile));
  elements.schemaDocumentFile.addEventListener("change", () => loadFile(elements.schemaDocumentFile.files?.[0], elements.schemaDocument, elements.schemaDocumentName, elements.schemaDocumentFile));
  elements.schemaData.addEventListener("input", changed);
  elements.schemaDocument.addEventListener("input", changed);
  elements.loadSchemaExample.addEventListener("click", () => {
    window.toolboxAnalytics?.track("tool_start");
    elements.schemaData.value = '{"name":"Ada","age":36,"active":true}';
    elements.schemaDocument.value = JSON.stringify({
      "$schema": "https://json-schema.org/draft/2020-12/schema",
      type: "object",
      required: ["name", "active"],
      properties: {
        name: { type: "string", minLength: 1 },
        age: { type: "integer", minimum: 0 },
        active: { type: "boolean" }
      },
      additionalProperties: false
    }, null, 2);
    changed();
    validate();
  });
  elements.clearSchemaValidator.addEventListener("click", () => {
    elements.schemaData.value = "";
    elements.schemaDocument.value = "";
    elements.schemaDataName.textContent = "JSON instance";
    elements.schemaDocumentName.textContent = "Schema document";
    changed();
    elements.schemaData.focus();
  });
  elements.validateJsonSchema.addEventListener("click", validate);
  root.querySelectorAll("[data-schema-drop]").forEach(card => {
    card.addEventListener("dragover", event => {
      event.preventDefault();
      card.classList.add("is-dragging");
    });
    card.addEventListener("dragleave", () => card.classList.remove("is-dragging"));
    card.addEventListener("drop", event => {
      event.preventDefault();
      card.classList.remove("is-dragging");
      const isData = card.dataset.schemaDrop === "data";
      loadFile(
        event.dataTransfer.files?.[0],
        isData ? elements.schemaData : elements.schemaDocument,
        isData ? elements.schemaDataName : elements.schemaDocumentName,
        isData ? elements.schemaDataFile : elements.schemaDocumentFile
      );
    });
  });
  changed();
}
